/**
 * Section 11 — Order Flow.
 *
 * The "who's actually buying/selling right now?" surface. Two display
 * forms (live panel + per-candle ribbon), one source (Delta /v2/trades
 * polled every 5s and aggregated). Covers absorption detection, S/R
 * sources, and the R79 bucket-alignment that made the panel and ribbon
 * agree.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import WavesIcon from '@mui/icons-material/Waves';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideCodeBlock from '../GuideCodeBlock';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_order_flow: GuideSection = {
  id: 'order-flow',
  label: 'Order Flow',
  group: 'reference',
  icon: <WavesIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Order Flow</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Reads the actual transaction tape — every print from Delta's <code>/v2/trades</code> —
        and surfaces who's in control. Crypto-only (Delta) for now; depth-of-book equivalents for NSE/Zerodha
        live in the FNO section.
      </Typography>

      <H id="what-it-is">What it is</H>
      <P>
        Every executed trade has a "taker side" — the participant who crossed the spread to fill. A buy at
        the ask is a <strong>taker-buy</strong> (aggressive demand). A sell at the bid is a
        <strong> taker-sell</strong> (aggressive supply). Aggregating taker volumes over a window tells you
        which side is currently aggressive.
      </P>
      <P>
        Order Flow exposes this two ways:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Live OF panel</strong> — bottom-left of the chart. Bias pill + tape + direction tag + absorption tag. Polls every 3s.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Flow ribbon</strong> — bottom of the chart. Histogram, one bar per candle bucket, 7-state colour palette.</>} />
        </ListItem>
      </List>

      <H id="bias-math">Taker-bias math</H>
      <P>
        Both surfaces share the same computation:
      </P>
      <GuideCodeBlock label="Taker bias">
{`buyVol  = Σ size of trades where taker side = buy
sellVol = Σ size of trades where taker side = sell
total   = buyVol + sellVol
buyPct  = total > 0 ? buyVol / total : 0.5

bias =
  BULL    when buyPct ≥ 0.60
  BEAR    when buyPct ≤ 0.40
  NEUTRAL otherwise

windowBias (5-state, used by the OF direction synthesis below):
  STRONG_BUY  when buyPct ≥ 0.75
  BUY         when buyPct 0.60 – 0.75
  NEUTRAL     when 0.40 – 0.60
  SELL        when 0.25 – 0.40
  STRONG_SELL when buyPct ≤ 0.25`}
      </GuideCodeBlock>
      <P>
        The difference between the two surfaces is the <em>window</em> these sums are computed over:
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Surface</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Window</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><strong>Live OF panel · 30s button</strong></TableCell><TableCell>Rolling 30 seconds ending at now.</TableCell></TableRow>
          <TableRow><TableCell><strong>Live OF panel · 1m/5m/15m buttons</strong></TableCell><TableCell>R79: aligned to the <em>current in-progress candle bucket</em>. The panel's bias pill matches the Flow ribbon's rightmost bar.</TableCell></TableRow>
          <TableRow><TableCell><strong>Flow ribbon</strong></TableCell><TableCell>One bar per chart candle. 1m chart → 1-minute buckets. Each bar covers a full closed candle except the rightmost which is in progress.</TableCell></TableRow>
        </TableBody>
      </Table>

      <GuideCallout kind="info" title="Why R79 mattered">
        Before R79, the panel was rolling and the ribbon was per-candle, so the two could legitimately disagree — the
        panel might say BULL while the ribbon's last bar was BEAR. Looked like a bug, was actually two different
        windows. R79 aligned the panel buttons to the same candle buckets the ribbon uses.
      </GuideCallout>

      <H id="ribbon-states">Flow ribbon — 7 colour states</H>
      <P>
        Each ribbon bar is one of seven colour states determined by buyPct + trend (whether buyPct is rising or
        falling vs the previous bar):
      </P>
      <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', p: 1.25, my: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.75 }}>{`
State                  buyPct  trend     colour
─────────────────────  ──────  ────────  ──────────
Bulls in control       ≥ 0.75  ↑ or ⇆   deep green
Bulls holding          0.60+   ⇆         med green
Bulls fading           0.55+   ↓         pale green
Neutral                0.45 — 0.55       grey
Bears fading           ≤ 0.45  ↑         pale red
Bears holding          ≤ 0.40  ⇆         med red
Bears in control       ≤ 0.25  ↓ or ⇆   deep red
`}</Box>
      <P>
        Bar height is proportional to total taker volume (clipped to the 95th percentile so a single
        spike-volume bar doesn't squash everything else). Busy candles stand tall; quiet ones sit flush.
      </P>

      <H id="direction">Direction synthesis (LONG / SHORT / WAIT)</H>
      <P>
        The live OF panel also synthesises a <strong>direction tag</strong> that combines windowBias +
        latestRun (consecutive same-side trades at the tape tail) + absorption near an S/R level:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>LONG</strong> — windowBias ∈ {`{BUY, STRONG_BUY}`} AND latestRun.side='buy' with count ≥ 3 AND NOT (absorption=RESISTANCE_ABSORPTION).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>SHORT</strong> — windowBias ∈ {`{SELL, STRONG_SELL}`} AND latestRun.side='sell' with count ≥ 3 AND NOT (absorption=SUPPORT_ABSORPTION).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>LONG_HIGH / SHORT_HIGH</strong> — base direction matches AND absorption is the <em>opposing</em>-side type (support held under sell pressure → strengthens long). Marked with a ★.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>WAIT</strong> — signals conflict, NEUTRAL, or no run.</>} />
        </ListItem>
      </List>

      <H id="absorption">Absorption — detecting where bigger orders are hiding</H>
      <P>
        Absorption happens when a one-sided burst of taker volume <em>fails</em> to move price the way it
        normally would — meaning passive limit orders on the other side are absorbing the burst. Detection
        rule on the OF panel:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Split the trade tape into "last 10s" vs "rest of window". Compute per-second buy/sell volume in each.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Burst trigger: last-10s per-second rate is ≥ 2× the rest's rate.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>One-sided check: ≥ 60% of the burst is one side (sell-skew ≥ 0.60 or buy-skew ≥ 0.60).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Price proximity: the recent-window price band's midpoint is within 0.3% of an S/R level.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Level holding: for SUPPORT_ABSORPTION the recent low stayed above (level − 0.3%); for RESISTANCE_ABSORPTION the recent high stayed below (level + 0.3%).</>} />
        </ListItem>
      </List>
      <P>
        When all conditions are met, the OF panel shows a tag (S-abs for support, R-abs for resistance) and
        the direction synthesis upgrades to LONG_HIGH / SHORT_HIGH if it aligns.
      </P>

      <H id="sr-sources">S/R level sources</H>
      <P>
        Absorption needs an S/R reference to check proximity. earngush pulls these from:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Your open trades' stop-loss prices." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Your open trades' target prices." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>All your HVN levels for this symbol (drawn with the HVN tool on Charts — see <OpenInApp to="/app-guide#charts" label="Charts → HVN" />).</>} />
        </ListItem>
      </List>
      <GuideCallout kind="tip" title="No S/R levels yet?">
        Round 67g loosened absorption detection so it now fires on the volume signature + price-reversal
        pattern alone, even without S/R levels. With S/R levels added, the signal is upgraded (you see a
        confidence bump on the direction tag).
      </GuideCallout>

      <H id="history">History endpoint + retention</H>
      <P>
        Per-minute taker volumes are aggregated server-side and persisted in <code>delta_trade_buckets</code>.
        Retention: 3 days (older buckets auto-pruned daily). The Flow ribbon fetches history from
        <code> GET /v1/order-flow/history</code> on every chart load.
      </P>
      <P>
        Aggregator polls Delta every 5 seconds. Pinned per-symbol with an 80ms gap between symbols to stay
        well under Delta's rate limits.
      </P>

      <H id="reading">How to read Order Flow during a trade</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Entry confirmation</strong> — about to BUY? Glance at the live panel's bias pill. If it's BULL or STRONG_BUY, you have flow tailwind. NEUTRAL = ambiguous, your edge has to come from elsewhere.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Trend health check</strong> — in a long position, the ribbon to the right of your entry should stay green-ish. Sudden deep-red bars = supply hit + tighten your SL.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Absorption near your target</strong> — if R-abs appears just as price approaches your take-profit, it's likely your target won't get hit cleanly; consider scaling out manually.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Direction tag for exit timing</strong> — when LONG_HIGH switches to WAIT or SHORT, the momentum is fading; valid signal to lock in partial profit.</>} />
        </ListItem>
      </List>

      <GuideExample title="A real-time read — ETHUSD 5m">
        Live panel: ▲ 68% BULL · windowBias BUY · latestRun run 4↑ · S-abs · direction LONG ★ (LONG_HIGH)<br />
        Ribbon: last 4 bars all deep green or med green.<br />
        <br />
        Read: aggressive buyers in the last 5 min, 4 consecutive buy-trades at the tape tail, AND price
        recently held a support level under selling pressure. Strong confluence for a long entry — if you
        had a chart-side trigger lined up (e.g. an AvgCloud flip green), this is the moment to take it.
      </GuideExample>

      <H id="caveats">Caveats</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Crypto / Delta only. NSE/Zerodha doesn't expose taker-side per-print in its public APIs at this fidelity." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Low-volume pairs (HANAUSD-class symbols) fall back to 'last 25 trades over Xm' so you still see something. The panel labels this with an italic 'expanded' suffix." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Order Flow ≠ order book. It's transacted volume, not standing limit orders. Aggressive prints are visible; passive walls aren't (we infer them from absorption patterns)." />
        </ListItem>
      </List>
    </>
  ),
  searchIndex: [
    { heading: 'What it is',         anchor: 'what-it-is',   body: 'order flow taker bias buy sell delta trades' },
    { heading: 'Taker-bias math',    anchor: 'bias-math',    body: 'taker bias math buypct bull bear neutral window bias strong' },
    { heading: 'Ribbon — 7 states',  anchor: 'ribbon-states',body: 'ribbon seven states colour bulls bears in control holding fading neutral' },
    { heading: 'Direction synthesis',anchor: 'direction',    body: 'direction long short wait long high short high latest run absorption' },
    { heading: 'Absorption',         anchor: 'absorption',   body: 'absorption support resistance burst sr level holding' },
    { heading: 'S/R level sources',  anchor: 'sr-sources',   body: 'sr level sources open trade stop loss target hvn' },
    { heading: 'History endpoint',   anchor: 'history',      body: 'history endpoint retention delta trade buckets three day' },
    { heading: 'Reading during a trade', anchor: 'reading',  body: 'reading during trade entry confirmation trend health absorption exit timing' },
    { heading: 'Caveats',            anchor: 'caveats',      body: 'caveats crypto only low volume order book' },
  ],
};

export default Section_order_flow;
